import { ModelConfig } from '@rematch/core';
import { localSDK as client } from '../../../sdk';
import {
    PlatformAdminRow,
    PlatformSuperAdminEmailList,
    PlatformSuperAdminMeResponse
} from '../../../models/entities/platform/platformSuperAdmin';
import { getJwtPayload } from '../../../utils/jwt';
import { getActiveTenantIdFromSession, setActiveTenantIdInSession } from '../../../utils/tenantSession';

export type PlatformSuperAdminState = {
    isPlatformSuperAdmin: boolean;
    accessChecked: boolean;
    usePlatformAudit: boolean;
    activeTenantId: string | null;
    me: PlatformSuperAdminMeResponse | null;
    list: PlatformSuperAdminEmailList | null;
    rows: PlatformAdminRow[];
    lastError: string | null;
};

function parseApiErrorMessage(error: any): string {
    const raw = error?.message;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            if (parsed?.message) return parsed.message;
        } catch {
            /* plain text */
        }
        return raw;
    }
    return 'Request failed';
}

function buildRows(list: PlatformSuperAdminEmailList): PlatformAdminRow[] {
    const bootstrap = list.bootstrapEmailsFromEnvironment.map((email) => ({
        id: `bootstrap:${email}`,
        email,
        source: 'bootstrap' as const
    }));
    const database = list.databaseEmails.map((email) => ({
        id: `database:${email}`,
        email,
        source: 'database' as const
    }));
    return [...bootstrap, ...database];
}

export const platformSuperAdmin: ModelConfig<PlatformSuperAdminState> = {
    state: {
        isPlatformSuperAdmin: false,
        accessChecked: false,
        usePlatformAudit: false,
        activeTenantId: null,
        me: null,
        list: null,
        rows: [],
        lastError: null
    },
    reducers: {
        reset(state: PlatformSuperAdminState): PlatformSuperAdminState {
            return {
                ...state,
                isPlatformSuperAdmin: false,
                accessChecked: false,
                activeTenantId: null,
                me: null,
                list: null,
                rows: [],
                lastError: null
            };
        },
        setAccessFromJwt(state: PlatformSuperAdminState, isPlatformSuperAdmin: boolean): PlatformSuperAdminState {
            return { ...state, isPlatformSuperAdmin };
        },
        setAccessChecked(state: PlatformSuperAdminState, payload: { isPlatformSuperAdmin: boolean; me?: PlatformSuperAdminMeResponse | null }): PlatformSuperAdminState {
            return {
                ...state,
                accessChecked: true,
                isPlatformSuperAdmin: payload.isPlatformSuperAdmin,
                me: payload.me ?? null
            };
        },
        setList(state: PlatformSuperAdminState, list: PlatformSuperAdminEmailList): PlatformSuperAdminState {
            return { ...state, list, rows: buildRows(list), lastError: null };
        },
        setLastError(state: PlatformSuperAdminState, lastError: string | null): PlatformSuperAdminState {
            return { ...state, lastError };
        },
        setUsePlatformAudit(state: PlatformSuperAdminState, usePlatformAudit: boolean): PlatformSuperAdminState {
            return { ...state, usePlatformAudit };
        },
        setActiveTenantId(state: PlatformSuperAdminState, activeTenantId: string | null): PlatformSuperAdminState {
            setActiveTenantIdInSession(activeTenantId);
            return { ...state, activeTenantId };
        }
    },
    effects: (dispatch: any) => ({
        initTenantFromSession() {
            const stored = getActiveTenantIdFromSession();
            if (stored) {
                dispatch.platformSuperAdmin.setActiveTenantId(stored);
            }
        },
        async switchTenantView(tenantId: string | null) {
            dispatch.platformSuperAdmin.setActiveTenantId(tenantId);
            await dispatch.user.fetchUserInfo();
            await dispatch.businessPartners.fetchBusinessPartners();
            await Promise.allSettled([
                dispatch.shipments.fetchShipments(),
                dispatch.purchaseOrders.fetchPurchaseOrders(),
                dispatch.purchaseDemands.fetchPurchaseDemands()
            ]);
        },
        bootstrapFromToken(token: string | null) {
            if (!token) {
                dispatch.platformSuperAdmin.reset();
                return;
            }
            const claim = getJwtPayload(token)?.platformSuperAdmin === true;
            dispatch.platformSuperAdmin.setAccessFromJwt(claim);
            dispatch.platformSuperAdmin.initTenantFromSession();
            dispatch.platformSuperAdmin.confirmAccess();
        },
        async confirmAccess(_payload: void, state: any) {
            const token = state.authentication?.token as string | null;
            if (!token) {
                dispatch.platformSuperAdmin.setAccessChecked({ isPlatformSuperAdmin: false, me: null });
                return;
            }
            try {
                const me = await client.superAdmin().fetchMe();
                dispatch.platformSuperAdmin.setAccessChecked({ isPlatformSuperAdmin: true, me });
                dispatch.platformSuperAdmin.setAccessFromJwt(true);
            } catch {
                const claim = getJwtPayload(token)?.platformSuperAdmin === true;
                dispatch.platformSuperAdmin.setAccessChecked({ isPlatformSuperAdmin: claim, me: null });
                dispatch.platformSuperAdmin.setAccessFromJwt(claim);
            }
        },
        async fetchPlatformAdmins() {
            const list = await client.superAdmin().listPlatformAdmins();
            dispatch.platformSuperAdmin.setList(list);
        },
        async addPlatformAdmin(email: string) {
            try {
                await client.superAdmin().addPlatformAdmin(email);
                dispatch.platformSuperAdmin.setLastError(null);
                await dispatch.platformSuperAdmin.fetchPlatformAdmins();
            } catch (error: any) {
                dispatch.platformSuperAdmin.setLastError(parseApiErrorMessage(error));
                throw error;
            }
        },
        async revokePlatformAdmin(email: string) {
            try {
                await client.superAdmin().revokePlatformAdmin(email);
                dispatch.platformSuperAdmin.setLastError(null);
                await dispatch.platformSuperAdmin.fetchPlatformAdmins();
            } catch (error: any) {
                dispatch.platformSuperAdmin.setLastError(parseApiErrorMessage(error));
                throw error;
            }
        }
    })
};
